import { useCallback, useEffect, useState, type RefCallback } from 'react';

/**
 * Observes an element's content-box dimensions via ResizeObserver.
 * Returns a callback ref to attach to the target element, plus the
 * measured width/height. Re-observes automatically when the element changes
 * (e.g. due to conditional rendering or list reordering).
 */
export function useResizeObserver(): [RefCallback<Element>, { width: number | undefined; height: number | undefined }] {
  const [element, setElement] = useState<Element | null>(null);
  const [width, setWidth] = useState<number | undefined>();
  const [height, setHeight] = useState<number | undefined>();

  const ref = useCallback((node: Element | null) => {
    setElement(node);
  }, []);

  useEffect(() => {
    if (element == null) {
      setWidth(undefined);
      setHeight(undefined);
      return;
    }

    const observer = new ResizeObserver(([entry]) => {
      setWidth(entry.contentRect.width);
      setHeight(entry.contentRect.height);
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);

  return [ref, { width, height }];
}
